import { useQuery } from '@tanstack/react-query';
import { formatQty } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { Field, field } from '../create/Field';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';

/** Exact-source, role-scoped reservation choices; this field surface contains no prices. */
export function TransferAllocationPicker({
  itemId,
  fromLocationId,
  value,
  onChange,
}: {
  itemId: string;
  fromLocationId: string;
  value: string;
  onChange: (value: string) => void;
}) {
  const trpc = useTRPC();
  const q = useQuery(
    trpc.inventoryReservations.transferChoices.queryOptions({ itemId, fromLocationId }),
  );
  return (
    <div className="space-y-2">
      <Field label="Move stock for">
        <select
          className={field}
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">Free stock · no work order allocation</option>
          {value &&
          !q.data?.choices.some((choice) => choice.workOrderLineId === value) ? (
            <option value={value}>Selected allocation · refresh required</option>
          ) : null}
          {(q.data?.choices ?? []).map((choice) => (
            <option key={choice.workOrderLineId} value={choice.workOrderLineId}>
              Work order #{choice.workOrderNumber} · {choice.taskName || 'General work'} ·{' '}
              {formatQty(choice.reservedHundredths)} reserved here
            </option>
          ))}
        </select>
      </Field>
      {q.isError || q.isPaused ? (
        <Notice tone="warn">
          Reservation choices could not refresh. A transfer is validated again before it
          changes stock.
          <Button size="sm" variant="ghost" onClick={() => void q.refetch()}>
            Refresh allocations
          </Button>
        </Notice>
      ) : q.isPending ? (
        <p className="text-xs text-muted-foreground">Loading work order allocations…</p>
      ) : null}
      <p className="text-xs text-muted-foreground">
        Choose a specific allocation to move its stock and reservation together.
      </p>
    </div>
  );
}
